<template>
  <div class="image-carousel" :class="`layout-${layout}`">
    <div v-if="layout === 'side-by-side'" class="carousel-text">
      <h2>{{ slides[activeIndex].heading }}</h2>
      <p>{{ slides[activeIndex].text }}</p>
      <a
        v-if="slides[activeIndex].linkUrl"
        class="carousel-link"
        :href="slides[activeIndex].linkUrl"
        target="_blank"
        rel="noopener"
      >{{ slides[activeIndex].linkText || 'Take a look' }}</a>
      <slot name="cta" />
    </div>

    <div class="carousel-visual" @mouseenter="pauseAutoplay" @mouseleave="resumeAutoplay" @focusin="pauseAutoplay" @focusout="resumeAutoplay">
      <div class="carousel-frame">
        <img :src="slides[activeIndex].src" :alt="slides[activeIndex].alt">
        <button class="carousel-arrow carousel-arrow-prev" title="Previous slide" aria-label="Previous slide" @click="prev"><PreviousIcon /></button>
        <button class="carousel-arrow carousel-arrow-next" title="Next slide" aria-label="Next slide" @click="next"><NextIcon /></button>
      </div>
      <p v-if="layout === 'full' && slides[activeIndex].caption" class="carousel-caption">{{ slides[activeIndex].caption }}</p>
      <div class="carousel-dots">
        <button
          v-for="(slide, i) in slides"
          :key="slide.src"
          class="carousel-dot"
          :class="{ active: i === activeIndex }"
          :title="`Show slide ${i + 1}`"
          :aria-label="`Show slide ${i + 1}`"
          @click="goTo(i)"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Reusable image slideshow, each slide fully self-contained.
// layout="full": image + its own caption underneath.
// layout="side-by-side": a text column (heading/text/CTA slot) beside the image,
// swapping together with the slide.
import PreviousIcon from '@/assets/icons/previous.vue'
import NextIcon from '@/assets/icons/next.vue'

export default {
  name: 'ImageCarousel',
  components: { PreviousIcon, NextIcon },
  props: {
    slides: {
      type: Array,
      required: true,
      validator: (slides) => slides.every(s => s && typeof s.src === 'string')
    },
    layout: {
      type: String,
      default: 'full',
      validator: (value) => ['side-by-side', 'full'].includes(value)
    },
    // Opt-in: off by default so this component's behavior doesn't silently
    // change wherever else it gets reused. Paused on hover/focus/hidden tab,
    // and skipped entirely for prefers-reduced-motion.
    autoplay: {
      type: Boolean,
      default: false
    },
    autoplayInterval: {
      type: Number,
      default: 5000
    }
  },
  data() {
    return {
      activeIndex: 0,
      autoplayTimer: null,
      autoplayPaused: false
    }
  },
  mounted() {
    if (this.autoplay) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      this.startAutoplay()
    }
  },
  beforeUnmount() {
    this.stopAutoplay()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    prev() {
      this.activeIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length
      this.startAutoplay()
    },
    next() {
      this.activeIndex = (this.activeIndex + 1) % this.slides.length
      this.startAutoplay()
    },
    goTo(i) {
      this.activeIndex = i
      this.startAutoplay()
    },
    startAutoplay() {
      if (!this.autoplay || this.slides.length < 2) return
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
      this.stopAutoplay()
      this.autoplayTimer = setInterval(() => {
        if (!this.autoplayPaused) this.next()
      }, this.autoplayInterval)
    },
    stopAutoplay() {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    },
    pauseAutoplay() {
      this.autoplayPaused = true
    },
    resumeAutoplay() {
      this.autoplayPaused = false
    },
    handleVisibilityChange() {
      if (document.hidden) this.stopAutoplay()
      else this.startAutoplay()
    }
  }
}
</script>

<style scoped>
.image-carousel.layout-full {
  max-width: 760px;
  margin: 0 auto;
}

.image-carousel.layout-side-by-side {
  display: flex;
  align-items: center;
  gap: 3rem;
}

.carousel-text {
  flex: 1 1 0;
  min-width: 0;
}

/* Inherits the caller's display face so the carousel matches whatever page
   it sits on. Sized as a caption on the image, not a second page heading. */
.carousel-text h2 {
  font-family: inherit;
  font-size: 1.4rem;
  font-weight: 600;
  line-height: 1.25;
  margin-bottom: 0.5rem;
  color: var(--color-admin-text);
}

.carousel-text p {
  font-size: 0.98rem;
  line-height: 1.55;
  color: var(--color-muted);
  margin-bottom: 1.75rem;
}

/* Optional per-slide link, for slides that show something the visitor can
   go and see for themselves. */
.carousel-link {
  display: inline-block;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-accent);
  text-decoration: none;
  border-bottom: 1px solid currentColor;
  padding-bottom: 1px;
  transition: opacity 0.2s;
}

.carousel-link:hover {
  opacity: 0.75;
}

.carousel-visual {
  flex: 1 1 0;
  min-width: 0;
}

.carousel-frame {
  position: relative;
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-light-gray);
  overflow: hidden;
}

.carousel-frame img {
  display: block;
  width: 100%;
  height: auto;
}

.carousel-caption {
  margin-top: 1rem;
  font-size: 1.05rem;
  color: var(--color-admin-text);
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: color-mix(in srgb, var(--color-white) 70%, transparent);
  color: var(--color-admin-text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
  transition: background-color 0.2s, transform 0.2s;
}

@media (hover: hover) {
  .carousel-arrow:hover {
    background: var(--color-white);
    transform: translateY(-50%) scale(1.1);
  }
}

.carousel-arrow:active {
  transform: translateY(-50%) scale(0.95);
}

/* Admin previews always show the desktop page */
@media screen and (max-width: 768px) {
  :where(.image-carousel:not(.preview-page *)) .carousel-arrow {
    width: 34px;
    height: 34px;
  }
}

.carousel-arrow-prev {
  left: 0.75rem;
}

.carousel-arrow-next {
  right: 0.75rem;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.carousel-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: none;
  padding: 0;
  background: var(--color-muted);
  cursor: pointer;
}

.carousel-dot.active {
  background: var(--color-accent);
}

@media (max-width: 768px) {
  .image-carousel.layout-side-by-side:where(:not(.preview-page *)) {
    flex-direction: column;
    text-align: center;
  }
}
</style>
